import React, { useContext, useState } from 'react'
import {
  DragDropContext,
  Draggable,
  Droppable,
  OnDragEndResponder,
} from 'react-beautiful-dnd'
import styled from 'styled-components'
import { Option, QuestionnaireContextParam, SelectTypeEnum } from './types'
import { GrowpIcon } from 'packages/components/GrowpIcon'
import { Checkbox, Input, Radio, Select } from '@seiue/ui'
import { FieldTypeEnum } from 'packages/sdks-next/growp'
import { DragContext, QuestionnaireContext } from './context'
import {
  changeMetaData,
  changeQuestionnaireData,
  isOptionsIncludeOther,
} from './utils'
import { CheckboxChangeEvent } from 'antd/es/checkbox'
import { cloneDeep } from '@seiue/util'
import { LabelEllipsis } from './LabelEllipsis'
import { getTheme } from '@/utils/theme'
const theme = getTheme()

/**
 * 单选|多选 - 选项设置
 *
 * @param param - 参数
 * @param param.options - 选项
 * @param param.otherRequire - 其他是否必填
 * @param param.type - 类型
 * @param param.currentIndex - 当前题目索引|包含描述和分页
 * @returns React.ReactNode
 */
export const OptionSetting: React.FC<{
  options: Option[]
  otherRequire?: boolean
  type: FieldTypeEnum.Single | FieldTypeEnum.Multi
  currentIndex: number
}> = ({ options, type, currentIndex }) => {
  const { questionnaires, setQuestionnaires, activeItemIndex, structIsChange } =
    useContext<QuestionnaireContextParam>(QuestionnaireContext)
  const currentQuestion = questionnaires
    ? questionnaires[currentIndex]
    : undefined
  if (!currentQuestion) return null

  const [isFocusIndex, setIsFocusIndex] = useState<number>()
  const { onChangeDragDisabled } = useContext(DragContext)

  const includeOther = isOptionsIncludeOther(options)

  const showCloseIcon =
    (type === FieldTypeEnum.Single && options.length > 1) ||
    (type === FieldTypeEnum.Multi && options.length > 2)

  const onAddOption = (isOther = false) => {
    const nextOptionIndex = options.length + 1
    const newOptions = [
      ...options,
      isOther
        ? {
            name: '其它',
            label: '其它',
            value: '其它',
          }
        : {
            name: `选项${nextOptionIndex}`,
            label: `选项${nextOptionIndex}`,
            value: `选项${nextOptionIndex}`,
          },
    ]
    const newQuestion = {
      ...currentQuestion,
      options: newOptions,
      metadata: {
        otherRequire: isOther ? true : undefined,
        ...currentQuestion.metadata,
      },
    }
    const newParams = changeQuestionnaireData(
      questionnaires,
      currentIndex,
      newQuestion
    )
    setQuestionnaires([...newParams])
    structIsChange && structIsChange()
  }

  const onDeleteOption = (index: number) => {
    const cloneOpts = cloneDeep(options)
    cloneOpts.splice(index, 1)
    const newQuestionnaires = changeQuestionnaireData(
      questionnaires,
      activeItemIndex || 0,
      {
        ...currentQuestion,
        options: cloneOpts,
      }
    )
    setQuestionnaires([...newQuestionnaires])
    structIsChange && structIsChange()
  }

  const onChangeOtherRequire = (e: CheckboxChangeEvent) => {
    const newQuestionnaire = changeMetaData(questionnaires, currentIndex, {
      otherRequire: e.target.checked,
    })
    setQuestionnaires([...newQuestionnaire])
  }

  const onChangeOptionLabel = (param: Option, index: number) => {
    const preOptions = cloneDeep(options)
    preOptions.splice(index, 1, param)
    const newQuestionnaires = changeQuestionnaireData(
      questionnaires,
      activeItemIndex || 0,
      {
        ...currentQuestion,
        options: preOptions,
      }
    )
    setQuestionnaires([...newQuestionnaires])
  }

  const onDragEnd: OnDragEndResponder = (result) => {
    const { source, destination } = result
    if (
      source.droppableId !== 'options' ||
      destination?.droppableId !== 'options' ||
      source.index === destination.index
    ) {
      return
    }
    const options = currentQuestion.options || []
    const dragItem = options[source.index]
    options.splice(source.index, 1)
    options.splice(destination.index, 0, dragItem)
    structIsChange && structIsChange()
  }
  return (
    <Wrapper
      onMouseEnter={(e) => {
        e.stopPropagation()
        onChangeDragDisabled(true)
      }}
      onMouseLeave={(e) => {
        e.stopPropagation()
        onChangeDragDisabled(false)
      }}
    >
      <DragDropContext onDragEnd={onDragEnd}>
        {activeItemIndex !== currentIndex &&
        currentQuestion.metadata?.selectType === SelectTypeEnum.DropDown ? (
          <div style={{ paddingLeft: '40px' }}>
            <Select
              placeholder="请选择"
              disabled
              options={options}
            />
          </div>
        ) : (
          <>
            <Droppable
              droppableId={`options`}
              direction="vertical"
            >
              {(provided) => {
                return (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {options.map((option, index) => {
                      return (
                        <Draggable
                          draggableId={`${index}`}
                          index={index}
                          key={index}
                          isDragDisabled={currentQuestion.isUserField}
                        >
                          {(provided) => (
                            <OptionWrapper
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <OptionLeftWrapper>
                                <DragIconWrapper>
                                  {!currentQuestion.isUserField ? (
                                    <GrowpIcon
                                      name="DragVertical"
                                      className="drag-vertical-icon"
                                    />
                                  ) : null}
                                </DragIconWrapper>
                                {option.value === '其它' ? (
                                  <>
                                    {type === FieldTypeEnum.Single ? (
                                      <Radio disabled />
                                    ) : (
                                      <Checkbox
                                        disabled
                                        style={{ marginRight: '8px' }}
                                      />
                                    )}
                                    <OtherWrapper>
                                      <OtherLabel
                                        style={{
                                          color: '#152529',
                                        }}
                                      >
                                        其它
                                      </OtherLabel>
                                      <Input
                                        disabled
                                        style={{
                                          width: '514px',
                                        }}
                                      />
                                      <Checkbox
                                        style={{ marginLeft: '12px' }}
                                        onChange={onChangeOtherRequire}
                                        checked={
                                          currentQuestion.metadata?.otherRequire
                                        }
                                      />
                                      <OtherRequired>必填项</OtherRequired>{' '}
                                    </OtherWrapper>
                                  </>
                                ) : (
                                  <div
                                    className={
                                      currentQuestion.isUserField
                                        ? undefined
                                        : isFocusIndex === index &&
                                          activeItemIndex === currentIndex
                                        ? 'customer-input-wrapper-focused'
                                        : 'customer-input-wrapper'
                                    }
                                  >
                                    <OptionLabelInput>
                                      {currentQuestion.isUserField ? (
                                        <LabelEllipsis
                                          type={type}
                                          label={option.label}
                                        />
                                      ) : (
                                        <Input
                                          onFocus={() => setIsFocusIndex(index)}
                                          onBlur={() =>
                                            setIsFocusIndex(undefined)
                                          }
                                          underlined
                                          disabled={
                                            activeItemIndex !== currentIndex ||
                                            currentQuestion.isUserField
                                          }
                                          addonBefore={
                                            type === FieldTypeEnum.Single ? (
                                              <Radio disabled />
                                            ) : (
                                              <Checkbox disabled />
                                            )
                                          }
                                          value={option.label}
                                          onChange={(e) =>
                                            onChangeOptionLabel(
                                              {
                                                ...option,
                                                label: e.target.value,
                                                name: e.target.value,
                                              },
                                              index
                                            )
                                          }
                                        />
                                      )}
                                    </OptionLabelInput>
                                  </div>
                                )}
                              </OptionLeftWrapper>
                              {activeItemIndex === currentIndex &&
                              !currentQuestion.isUserField ? (
                                <CloseIconWrapper
                                  onClick={() => {
                                    if (showCloseIcon) {
                                      onDeleteOption(index)
                                    }
                                  }}
                                  className={
                                    isFocusIndex === index &&
                                    activeItemIndex === currentIndex
                                      ? 'show-close-icon'
                                      : 'close-icon'
                                  }
                                >
                                  {showCloseIcon && (
                                    <GrowpIcon
                                      name="Close"
                                      className={
                                        isFocusIndex === index &&
                                        activeItemIndex === currentIndex
                                          ? 'show-close-icon'
                                          : 'close-icon'
                                      }
                                      color={theme.text._2}
                                    />
                                  )}
                                </CloseIconWrapper>
                              ) : null}
                            </OptionWrapper>
                          )}
                        </Draggable>
                      )
                    })}
                    {provided.placeholder}
                  </div>
                )
              }}
            </Droppable>
            {activeItemIndex === currentIndex &&
            !currentQuestion.isUserField ? (
              <AddOptionWrapper>
                <AddWrapper>
                  {type === FieldTypeEnum.Single ? (
                    <Radio disabled />
                  ) : (
                    <Checkbox disabled />
                  )}

                  <AddBtnWrapper>
                    <AddButton onClick={() => onAddOption()}>
                      添加选项
                    </AddButton>
                    {!includeOther && (
                      <>
                        或
                        <AddButton
                          style={{ marginLeft: '12px' }}
                          onClick={() => onAddOption(true)}
                        >
                          添加「其它」
                        </AddButton>
                      </>
                    )}
                  </AddBtnWrapper>
                </AddWrapper>
              </AddOptionWrapper>
            ) : null}
          </>
        )}
      </DragDropContext>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
`

const OptionLeftWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  .customer-input-wrapper {
    /* border: 1px solid transparent; */
  }
  .customer-input-wrapper-focused,
  .customer-input-wrapper:hover {
    border-bottom: 1px solid ${(props) => props.theme.brand._1};
  }
`

const OptionWrapper = styled.div`
  width: 100%;
  height: 26px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-left: 16px;
  & .drag-vertical-icon {
    margin-right: 8px;
    display: none;
  }
  &:hover .drag-vertical-icon {
    display: block;
  }
  & .close-icon {
    display: none;
  }
  & .show-close-icon {
    display: flex;
  }
  &:hover .close-icon {
    display: flex;
  }
  &&& {
    .ant-input-group-wrapper {
      width: 648px;
      border-bottom: 1px solid transparent;
    }
    /* .ant-input-group-wrapper-focused,
    .ant-input-group-wrapper:hover {
      border-bottom: 1px solid ${(props) => props.theme.brand._1} !important;
    } */
    .ant-input-group {
      .ant-input,
      .ant-input-group-addon {
        background-color: transparent;
        border: none;
        height: 26px;
      }
      .ant-input-group-addon {
        padding: 0 0;
        width: 20px;
      }
      .ant-input {
        padding-left: 0;
        max-width: 600px;
      }
      .ant-checkbox-wrapper {
        margin-right: 8px;
      }
    }
  }
`

const DragIconWrapper = styled.div`
  width: 24px;
  height: 16px;
`

const CloseIconWrapper = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

const AddOptionWrapper = styled.div`
  width: 100%;
  padding: 0 0 0 40px;
`

const AddBtnWrapper = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => props.theme.text._2};
`

const AddButton = styled.span`
  color: ${(props) => props.theme.brand._1};
  margin-right: 12px;
  cursor: pointer;
`

const AddWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  .ant-checkbox-wrapper {
    margin-right: 8px;
  }
`

const OtherWrapper = styled.div`
  .ant-input {
    /* width: 514px !important; */
    height: 32px !important;
  }
`

const OtherLabel = styled.span`
  display: inline-block;
  width: 28px;
  margin-right: 12px;
`

const OtherRequired = styled.span`
  width: 42px;
  margin-left: 8px;
`

const OptionLabelInput = styled.div`
  &&& {
    .ant-input {
      max-width: 620px !important;
      padding-right: 0 !important;
      color: ${(props) => props.theme.text._1};
    }
  }
`
